import { HandBack, activeStatuses, ecId, inventoryId, record, upper, itemField, type JanusCtx, type Row } from '../ability/context';
import { encounterField, inventoryField } from '../../shared/array-types';
import { boundaryVerdict, heldInventoryRows, type GmBoundaryVerdict } from '../statuses/gm-boundary';
import { isOperatorStatus, isTransportStatus, removeTransportLink } from '../statuses/riding-removal';
import { statusCasterId, statusItemId, removeStatusFromRow, statusRemovalBefore, STATUS_IDS } from '../statuses/expiry';
import { statusScalar } from '../actions/activity';
import { resolveTemporaryChargeRemovals } from '../statuses/exact-removal-tails';
import { fullDefinition } from '../reactions/php-row-rules';
import { deathStatusRows } from '../death/cleanup';
import { retireDeathStatus } from '../death/status-removal';
import { HUNKER_STATUS } from '../actions/hunker';
import { removeStoredStatus } from '../ability/statuses/status-effect-removal-helpers';

import { statusRows as moveTailStatusRows } from '../statuses/expiry';
import { normalizePhpListish } from '../../shared/php-listish';
import { transportInventories as moveTailInventories } from '../statuses/riding-removal';
type MoveTailRow = import('../ability/context').Row;

export interface InventoryMoveTails {
    before: InventoryDelete['before'];
    verdict: GmBoundaryVerdict;
    memberExpectedRevs?: Record<string, number>;
    appliedFrame?: import('../transport/applied-frame').AppliedFrameResult;
}

function moveTransportDue(source: MoveTailRow, encounter: MoveTailRow): boolean {
    const id = inventoryId(source);
    const holders = [source, encounter, ...moveTailInventories(encounter),
        ...normalizePhpListish(encounter.encounter_creatures_arr).map(record).filter((row): row is MoveTailRow => !!row)];
    return holders.some(owner => (['target', 'caster'] as const).some(side => moveTailStatusRows(owner, side).some(status =>
        isTransportStatus(status) && Number(statusScalar(status, 'status_deleted') ?? 0) === 0
        && (owner === source || statusCasterId(status) === id || upper(statusScalar(status, 'status_target_id')) === id))));
}

/** Only a whole carrier leaving the floor (or merging away) retires its links.
 * Partial stacks keep their original carrier, statuses and support. */
export function inventoryMoveTailDue(source: MoveTailRow, encounter: MoveTailRow | null, destination: string, quantity: number, merge: string | null): boolean {
    if (!encounter || quantity < Number(inventoryField(source, 'item_qty') ?? 1)) return false;
    const id = inventoryId(source), holder = upper(inventoryField(source, 'possess_id'));
    if (!merge && (holder !== upper(encounterField(encounter, 'encounter_id')) || holder === upper(destination))) return false;
    const members = normalizePhpListish(encounter.encounter_creatures_arr).map(record).filter((row): row is MoveTailRow => !!row);
    return moveTransportDue(source, encounter)
        || members.some(member => moveTailStatusRows(member, 'target').some(status =>
            Number(statusScalar(status, 'status_deleted') ?? 0) === 0 && statusItemId(status) === HUNKER_STATUS && statusCasterId(status) === id));
}

/** The move and Delete share one transport/support/charge resolver. Only this
 * private field view loses the carrier; the held source survives a pickup. */
export function planInventoryMoveTails(ctx: JanusCtx, id: string, destination: string, quantity: number, merge: string | null): InventoryMoveTails {
    const target = heldInventoryRows(ctx).get(upper(id));
    if (!target) throw new HandBack('inventory-move:row-not-held');
    const field = (key: string): unknown => (ctx.read.inventoryField ?? inventoryField)(target, key);
    const before = { possess_id: upper(field('possess_id')), item_qty: Number(field('item_qty') ?? 1),
        inventory_deleted: Number(field('inventory_deleted') ?? 0),
        status_ids: activeStatuses(ctx, target).map(status => upper(statusScalar(status, 'status_id'))).sort() };
    if (inventoryMoveTailDue(target, ctx.encounter, destination, quantity, merge)) {
        try {
            // Delete and moves use the same retiring-carrier support view.
            // A move never serializes that private inventory tombstone.
            const statuses = activeStatuses(ctx, target);
            if (!statuses.some(isTransportStatus) && moveTransportDue(target, ctx.encounter))
                throw new HandBack('inventory-move:transport-carrier-status-not-held');
            // The shared transport primitive retires the pair. A rider-side
            // connection carrying additional effects needs a larger exact loss
            // plan; keep every row intact until that closure is supported.
            const connections = new Set(statuses.filter(isTransportStatus).map(status => upper(statusScalar(status, 'status_connected_id'))).filter(Boolean));
            for (const member of ctx.members.values()) for (const peer of activeStatuses(ctx, member))
                if (!isTransportStatus(peer) && connections.has(upper(statusScalar(peer, 'status_connected_id'))))
                    throw new HandBack('inventory-move:transport-connected-tail-not-ported');
            Object.assign(record(target.inventory) ?? target, { inventory_deleted: 1, item_qty: 0 });
            planInventoryRemovalTails(ctx, target, id, statuses);
            const verdict = boundaryVerdict(ctx);
            if (verdict.inventory?.length || verdict.recovery || verdict.spawnRows || verdict.shapeshiftInventory?.length)
                throw new HandBack('inventory-move:follow-up-persistence-not-ported');
            const skipped = verdict.rows.find(row => row.roll_type_1 === 'janus_unported_skip');
            if (skipped) throw new HandBack(`inventory-move:tail-not-ported:${String(skipped.rolls_full ?? '')}`);
            return { before, verdict };
        } catch (error) {
            if (error instanceof HandBack) throw new HandBack(error.reason.replace(/^inventory-delete:/, 'inventory-move:'));
            throw error;
        }
    }
    return { before, verdict: boundaryVerdict(ctx) };
}

export interface InventoryDelete {
    inventory_id: string;
    before: { possess_id: string; item_qty: number; inventory_deleted: number; status_ids: string[] };
}

/** Floor Delete is a prewrite action, distinct from breaking an object. Its
 * source statuses remain, except the transport pairs the old delete retired. */
export function planInventoryDelete(ctx: JanusCtx, id: string, log = true, creatorId?: string): GmBoundaryVerdict {
    const target = heldInventoryRows(ctx).get(upper(id));
    if (!target) throw new HandBack('inventory-delete:row-not-held');
    const field = (key: string): unknown => (ctx.read.inventoryField ?? inventoryField)(target, key);
    if (Number(field('inventory_deleted') ?? 0)) throw new HandBack('inventory-delete:already-deleted');
    const encounterId = upper(encounterField(ctx.encounter, 'encounter_id'));
    if (creatorId) {
        const rows = heldInventoryRows(ctx), seen = new Set<string>();
        let parent = upper(field('possess_id'));
        while (parent !== upper(creatorId)) {
            if (!parent || seen.has(parent)) throw new HandBack('inventory-delete:creator-owner-not-held');
            seen.add(parent);
            const holder = rows.get(parent);
            if (!holder) throw new HandBack('inventory-delete:creator-owner-not-held');
            parent = upper((ctx.read.inventoryField ?? inventoryField)(holder, 'possess_id'));
        }
    } else if (upper(field('possess_id')) !== encounterId) throw new HandBack('inventory-delete:carried-item-not-ported');
    if (upper(field('inventory_encounter_creature_id'))) throw new HandBack('inventory-delete:corpse-link-not-ported');
    const statuses = activeStatuses(ctx, target);
    // A creator session has carried rows and PC state, but no encounter geometry
    // or resident riders. Refuse a transport before the action can save anything.
    if (creatorId && statuses.some(isTransportStatus)) throw new HandBack('inventory-delete:creator-transport-not-held');
    for (const status of statuses) {
        const definition = fullDefinition(ctx.read.definition(statusItemId(status)), 'any') ?? fullDefinition(record(status.status_arr), 'any');
        if (!definition) throw new HandBack('inventory-delete:status-definition-not-held');
        if (statusItemId(status) === 'E49AA8BA-1F60-4FD8-B3C0-852BBA3978C3')
            throw new HandBack('inventory-delete:crafting-cleanup-not-ported');
    }
    const inventoryDelete: InventoryDelete = { inventory_id: inventoryId(target), before: {
        possess_id: upper(field('possess_id')), item_qty: Number(field('item_qty') ?? 1),
        inventory_deleted: Number(field('inventory_deleted') ?? 0),
        status_ids: statuses.map(status => upper(statusScalar(status, 'status_id'))).sort(),
    } };
    // Only the action's field view changes: exit/support geometry must see the
    // retiring carrier absent, rather than landing the rider back on that object.
    Object.assign(record(target.inventory) ?? target, { inventory_deleted: 1, item_qty: 0 });
    planInventoryRemovalTails(ctx, target, id, statuses);
    if (log) ctx.rows.push({ roll_type_1: 'inventory_deleted', roll_type_2: null, roll_item_id: inventoryId(target),
        roll_by: encounterId, roll_to: null, roll_result: 0, rolls_full: null,
        dice: { dice: 0, die: 0, mod: 0, adv_dis_stacks: 0, dc: 0 } });
    return { ...boundaryVerdict(ctx), inventoryDelete };
}

/** One synchronous children-first decision for a creator's complete selected subtree.
 * The caller provides field views, so a failed child never changes the held tree. */
export function planCreatorInventoryDelete(ctx: JanusCtx, rootId: string, creatorId: string): InventoryDelete[] {
    const rows = heldInventoryRows(ctx), plans: InventoryDelete[] = [], visiting = new Set<string>();
    const visit = (id: string): void => {
        if (visiting.has(id)) throw new HandBack('inventory-delete:creator-possess-cycle');
        visiting.add(id);
        for (const [childId, child] of rows) if (upper((ctx.read.inventoryField ?? inventoryField)(child, 'possess_id')) === id) visit(childId);
        // The creator holds raw PC and carried-row statuses, but no encounter
        // support geometry. A due external dependency cannot become an omitted tail.
        const row = rows.get(id);
        let dependent = row && moveTailStatusRows(row, 'caster').some(status => statusItemId(status) === HUNKER_STATUS || isTransportStatus(status));
        for (const member of ctx.members.values()) if (activeStatuses(ctx, member).some(status =>
            statusCasterId(status) === id && (statusItemId(status) === HUNKER_STATUS || isTransportStatus(status)))) dependent = true;
        if (dependent)
            throw new HandBack('inventory-delete:creator-follow-up-not-held');
        plans.push(planInventoryDelete(ctx, id, false, creatorId).inventoryDelete!);
    };
    visit(upper(rootId));
    if (ctx.statusesRemoved.length || ctx.chargesRemoved?.length || Object.keys(boundaryVerdict(ctx).ec).length)
        throw new HandBack('inventory-delete:creator-follow-up-not-ported');
    return plans;
}

function planInventoryRemovalTails(ctx: JanusCtx, target: MoveTailRow, id: string, statuses: MoveTailRow[]): void {
    // Require every survivor to be addressable before the shared transport
    // resolver can emit its boundary-style missing-rider skip.
    const holders = function* (): Generator<MoveTailRow> {
        yield ctx.encounter;
        yield* ctx.members.values();
        yield* heldInventoryRows(ctx).values();
    };
    for (const status of statuses.filter(isTransportStatus)) {
        const connection = upper(statusScalar(status, 'status_connected_id'));
        const pair: Array<{ owner: MoveTailRow; status: MoveTailRow }> = [];
        for (const owner of holders()) for (const peer of activeStatuses(ctx, owner))
            if (isTransportStatus(peer) && upper(statusScalar(peer, 'status_connected_id')) === connection) pair.push({ owner, status: peer });
        if (!connection || !pair.some(({ owner, status }) => isOperatorStatus(status) && ecId(owner)))
            throw new HandBack('inventory-delete:transport-rider-not-held');
        if (pair.some(({ status }) => !ctx.members.has(statusCasterId(status))))
            throw new HandBack('inventory-delete:transport-caster-not-held');
        for (const { owner, status } of pair) {
            const definition = fullDefinition(ctx.read.definition(statusItemId(status)), 'any') ?? fullDefinition(record(status.status_arr), 'any');
            if (!definition) throw new HandBack('inventory-delete:transport-definition-not-held');
            if (ctx.read.has(definition, 'object_destroyed_after_duration_complete'))
                throw new HandBack('inventory-delete:duration-inventory-destruction-not-ported');
            resolveTemporaryChargeRemovals(ctx, owner, definition, status);
        }
        if (!ctx.statusesRemoved.some(row => row.status_id === upper(statusScalar(status, 'status_id')))) removeTransportLink(ctx, target, status);
    }
    for (const member of ctx.members.values()) for (const status of activeStatuses(ctx, member)) {
        if (statusItemId(status) !== HUNKER_STATUS || statusCasterId(status) !== upper(id)) continue;
        const definition = fullDefinition(ctx.read.definition(HUNKER_STATUS), 'any') ?? fullDefinition(record(status.status_arr), 'any');
        if (!definition) throw new HandBack('inventory-delete:hunker-definition-not-held');
        if (ctx.read.has(definition, 'object_destroyed_after_duration_complete'))
            throw new HandBack('inventory-delete:duration-inventory-destruction-not-ported');
        removeStoredStatus(ctx, member, status, 'preserve');
    }
    // Landing/loss hooks can require a larger death, turn or summoned-creature
    // boundary. Never serialize only its status fragment as a successful Delete.
    if (ctx.deathTail?.length || ctx.dyingTail?.length || ctx.concentrationBreaks?.length || ctx.turnTransition
        || ctx.summons?.length || ctx.objectDamage?.length)
        throw new HandBack('inventory-delete:follow-up-persistence-not-ported');

}

export function recursiveDeleteSurvivors(deletedIds: readonly string[], creatureIds: readonly string[], physical: readonly Row[], catalog: readonly Row[]): Row[] {
    const deleted = new Set(deletedIds.map(upper)), creatures = new Set(creatureIds.map(upper));
    const ends = new Set(catalog.filter(row => itemField(row, 'ends_on_caster_death') === true).map(row => upper(itemField(row, 'item_id'))));
    const targetId = (row: Row): string => upper(statusScalar(row, 'status_target_id'));
    const connection = (row: Row): string => upper(statusScalar(row, 'status_connected_id'));
    const connections = new Set(physical.filter(row => deleted.has(targetId(row))).map(connection).filter(Boolean));
    return physical.filter(status => !deleted.has(targetId(status)) && (
        creatures.has(statusCasterId(status)) && ends.has(statusItemId(status))
        || deleted.has(statusCasterId(status)) && statusItemId(status) === HUNKER_STATUS
        || connections.has(connection(status)) && (isTransportStatus(status) || statusItemId(status) === STATUS_IDS.concentrating)
    ));
}

/** A recursive author delete owns the selected rows, not their surviving
 * beneficiaries. Internal rows have no removal tails; only survivors enter the
 * same exact-removal planner used by ordinary Delete. */
export function planRecursiveDelete(ctx: JanusCtx, deletedIds: readonly string[], creatureIds: readonly string[],
    physical: readonly Row[], catalog: readonly Row[], requiredTargets?: readonly string[]): GmBoundaryVerdict {
    const rowStart = ctx.rows.length;
    const deleted = new Set(deletedIds.map(upper));
    const holders = [ctx.encounter, ...ctx.members.values(), ...heldInventoryRows(ctx).values()];
    const targetId = (row: Row): string => upper(statusScalar(row, 'status_target_id'));
    const connection = (row: Row): string => upper(statusScalar(row, 'status_connected_id'));
    const ownerId = (row: Row): string => ecId(row) || inventoryId(row) || upper(encounterField(row, 'encounter_id'));
    const selected = recursiveDeleteSurvivors(deletedIds, creatureIds, physical, catalog)
        .filter(row => statusItemId(row) !== STATUS_IDS.concentrating);
    for (const id of requiredTargets ?? recursiveDeleteSurvivors(deletedIds, creatureIds, physical, catalog).map(targetId))
        if (!holders.some(row => ownerId(row) === upper(id))) throw new HandBack('recursive-delete:survivor-not-held');
    // Make retiring carriers unavailable to the shared support/landing search.
    for (const holder of holders) if (deleted.has(ownerId(holder))) {
        const fields = record(holder.inventory) ?? record(holder.encounter_creatures);
        if (fields) Object.assign(fields, record(holder.inventory) ? { inventory_deleted: 1, item_qty: 0 } : { encounter_creature_deleted: 1 });
    }
    // Clear internal projections without firing their hooks. Transport pairs are
    // retained until the outside operator has planned its landing.
    for (const status of physical.filter(row => deleted.has(targetId(row)) && !isTransportStatus(row)))
        for (const holder of holders) removeStatusFromRow(holder, upper(statusScalar(status, 'status_id')));
    for (const candidate of selected) {
        const owner = holders.find(row => ownerId(row) === targetId(candidate));
        if (!owner) continue; // A different encounter's plan owns this survivor.
        if (ctx.statusesRemoved.some(row => row.status_id === upper(statusScalar(candidate, 'status_id')))) continue;
        const status = activeStatuses(ctx, owner).find(row => upper(statusScalar(row, 'status_id')) === upper(statusScalar(candidate, 'status_id')));
        if (!status) throw new HandBack('recursive-delete:survivor-status-not-held');
        if (ctx.statusesRemoved.some(row => row.status_id === upper(statusScalar(status, 'status_id')))) continue;
        const definition = fullDefinition(ctx.read.definition(statusItemId(status)), 'any') ?? fullDefinition(record(status.status_arr), 'any');
        if (!definition) throw new HandBack('recursive-delete:status-definition-not-held');
        if (ctx.read.has(definition, 'object_destroyed_after_duration_complete'))
            throw new HandBack('recursive-delete:duration-inventory-destruction-not-ported');
        resolveTemporaryChargeRemovals(ctx, owner, definition, status);
        if (isTransportStatus(status) && !isOperatorStatus(status) && deleted.has(statusCasterId(status))) {
            // Its operator is being deleted: no living rider needs support.
            retireDeathStatus(ctx, holders, status);
        } else removeStoredStatus(ctx, owner, status, 'single', { allTargetPeers: true });
    }
    const connections = new Set(physical.filter(row => deleted.has(targetId(row))).map(connection).filter(Boolean));
    for (const owner of holders.filter(row => !deleted.has(ownerId(row)))) for (const status of activeStatuses(ctx, owner)) {
        if (statusItemId(status) !== STATUS_IDS.concentrating || !connections.has(connection(status))) continue;
        const remaining = physical.filter(row => connection(row) === connection(status) && !deleted.has(targetId(row))
            && !ctx.statusesRemoved.some(removal => removal.status_id === upper(statusScalar(row, 'status_id'))));
        if (remaining.every(row => statusItemId(row) === STATUS_IDS.concentrating)) removeStoredStatus(ctx, owner, status, 'single');
    }
    if (ctx.deathTail?.length || ctx.dyingTail?.length || ctx.concentrationBreaks?.length || ctx.turnTransition
        || ctx.summons?.length || ctx.objectDamage?.length || ctx.rows.slice(rowStart).some(row => row.roll_type_1 === 'janus_unported_skip'))
        throw new HandBack('recursive-delete:follow-up-persistence-not-ported');
    // Raw connection peers still retire temporary pools, without replaying loss hooks.
    for (const removal of ctx.statusesRemoved) {
        if (deleted.has(upper(removal.target))) continue;
        const status = physical.find(row => upper(statusScalar(row, 'status_id')) === removal.status_id);
        const owner = holders.find(row => ownerId(row) === upper(removal.target));
        if (!status || !owner) throw new HandBack('recursive-delete:removed-peer-not-held');
        const definition = ctx.read.definition(statusItemId(status)) ?? record(status.status_arr);
        if (!definition) throw new HandBack('recursive-delete:peer-definition-not-held');
        if (ctx.read.has(definition, 'object_destroyed_after_duration_complete'))
            throw new HandBack('recursive-delete:duration-inventory-destruction-not-ported');
        resolveTemporaryChargeRemovals(ctx, owner, definition, status);
        removal.before = statusRemovalBefore(status, removal.target);
    }
    const verdict = boundaryVerdict(ctx);
    for (const id of deleted) delete verdict.ec[id];
    verdict.statusesRemoved = verdict.statusesRemoved.filter(row => !deleted.has(upper(row.target)));
    verdict.chargesRemoved = verdict.chargesRemoved?.filter(row => !deleted.has(upper(row.holder)));
    return verdict;
}

/** Destruction and author deletion share the exact outside-row removal ledger. */
export function planDestructionRemoval(ctx: JanusCtx, commands: readonly import('./destroy-object').ObjectDestroyCommand[], catalog: readonly Row[]): GmBoundaryVerdict {
    const deleted = new Set<string>(), creatures = new Set<string>();
    const visit = (command: import('./destroy-object').ObjectDestroyCommand): void => {
        for (const id of command.frame.remove) deleted.add(upper(id));
        for (const id of command.spill?.delete ?? []) deleted.add(upper(id));
        if (command.corpse) {
            deleted.add(upper(command.corpse.encounter_creature_id)); creatures.add(upper(command.corpse.encounter_creature_id));
            for (const id of command.corpse.delete) deleted.add(upper(id));
        }
        for (const child of command.spill?.destroyed ?? []) visit(child);
    };
    commands.forEach(visit);
    for (const id of creatures) if (!ctx.members.has(id)) throw new HandBack('recursive-delete:corpse-not-held');
    const holders = [ctx.encounter, ...ctx.members.values(), ...heldInventoryRows(ctx).values()];
    const physical = deathStatusRows(holders);
    if (creatures.size && !catalog.length && physical.some(row => creatures.has(statusCasterId(row))
        && !deleted.has(upper(statusScalar(row, 'status_target_id'))))) throw new HandBack('recursive-delete:status-catalog-not-held');
    return planRecursiveDelete(ctx, [...deleted], [...creatures], physical, catalog);
}
